// @adr SPARK-0055-document-displays-use-one-public-boundary@3: Document thumbnail embedding is owned by the shared preview implementation.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { prepareDocumentThumbnail, type DocumentResource, type PreparedDocumentPreview } from "@/lib/document-resource";

/** Every document surface uses this lazy visual; pages supply only authorized file access. */
export function DocumentThumbnail({ resource, previewSrc, fallback, onImageError, compact = false, fit = "contain" }: {
  fit?: "contain" | "cover"; resource?: DocumentResource; previewSrc?: string | null; fallback: ReactNode; compact?: boolean; onImageError?: () => void;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const [state, setState] = useState<{ resource: DocumentResource; preview: PreparedDocumentPreview }>();
  const [failedImage, setFailedImage] = useState<string>();
  const validImage = previewSrc?.match(/^data:image\/(png|jpeg|webp|gif);base64,|^blob:/) ? previewSrc : undefined;
  const image = validImage !== failedImage ? validImage : undefined;
  useEffect(() => {
    if (image || !resource?.previewable || !ref.current || typeof IntersectionObserver === "undefined") return;
    let active = true;
    let prepared: PreparedDocumentPreview | undefined;
    const load = () => {
      void prepareDocumentThumbnail(resource).then(preview => {
        if (!active) { preview.dispose(); return; }
        prepared = preview;
        setState({ resource, preview });
      }).catch(() => { /* Keep the file label; opening reports errors and offers retry. */ });
    };
    const observer = new IntersectionObserver(entries => {
      if (!entries.some(entry => entry.isIntersecting)) return;
      observer.disconnect(); load();
    });
    observer.observe(ref.current);
    return () => { active = false; observer.disconnect(); prepared?.dispose(); };
  }, [resource, image]);
  const preview = state?.resource === resource ? state?.preview : undefined;
  const src = image ?? preview?.thumbnail;
  const scale = compact ? 0.06 : 0.14;
  return <span ref={ref} className="relative flex h-full w-full items-center justify-center overflow-hidden">
    {src && src !== failedImage ? <img src={src} alt="" loading="lazy" className={`h-full w-full ${fit === "cover" ? "object-cover" : "object-contain"}`} onError={() => { setFailedImage(src); onImageError?.(); }} />
      : preview?.html ? <span aria-hidden style={{ width: 800 * scale, height: 1000 * scale }} className="relative overflow-hidden">
        <iframe title={resource?.name} tabIndex={-1} sandbox="" srcDoc={preview.html} className="pointer-events-none absolute left-0 top-0 border-0" style={{ width: 800, height: 1000, transform: `scale(${scale})`, transformOrigin: "top left" }} />
      </span> : preview?.excerpt ? <span aria-hidden className="h-full overflow-hidden whitespace-pre-wrap text-smycles-micro text-left">{preview.excerpt}</span> : fallback}
  </span>;
}
